/**
 * WST-865 — recover from chunk-load failures caused by a stale app shell.
 *
 * A browser session that has been open across a deploy still runs the old index.html and
 * asks for hashed chunks by their old names. `eb deploy` replaces /var/app/current
 * wholesale, so the previous build's chunks are gone the moment the new one lands; the
 * request 404s and CloudFront's SPA fallback answers with index.html. Webpack then either
 * raises a ChunkLoadError or hands the HTML to the JS parser, which dies on the `<`.
 * (Sentry WT-PORTAL-EV / 8D / 5E / FD: 525 events, ~170 users in 60 days. The most recent
 * event ran release 2026-06-16…e87c18de while production had served 2026-07-06…59fed3be
 * since July — a two-month-old shell.)
 *
 * The fix is to reload once, so the browser fetches the current index.html and with it the
 * chunk hashes that actually exist. The reload is guarded by a sessionStorage timestamp:
 * if reloading did not help, we must not do it again (see RECOVERY_GUARD_MS).
 *
 * Deliberately NOT done here:
 *  - the URL is never rewritten (no cache-busting query param): the app carries a
 *    load-bearing `?magic=` magic-token auth parameter that must survive untouched;
 *  - `preventDefault()` is never called, so Sentry's own handlers still see the error;
 *  - the breadcrumb is logged with console.warn, not console.error, to avoid minting a
 *    new error event stream.
 */

import * as Sentry from '@sentry/react';

/** sessionStorage key holding the epoch-ms timestamp of the last recovery reload. */
export const RECOVERY_MARKER_KEY = 'wt_chunk_recovery_at';

/**
 * How long after a recovery reload we refuse to reload again.
 *
 * A cold reload of this app — index.html, the entry chunks, and the auth bootstrap — is
 * comfortably done inside 30s even on a slow mobile connection, so a second chunk failure
 * within that window means the reload did *not* fix it: a genuinely broken deploy, a chunk
 * that is permanently missing, or an offline user. In that case we let the error propagate
 * normally: Sentry records it and the user sees a real failure instead of a refresh loop,
 * which would be far worse than the original bug. Conversely 30s is short enough that a
 * chunk failure later in the same session (e.g. the user opens a new lazy route after the
 * *next* deploy) still gets its own recovery.
 */
export const RECOVERY_GUARD_MS = 30_000;

/** Hashed build assets: /static/js/3897.f289481e.chunk.js, /static/css/8417.a1b2.chunk.css. */
const CHUNK_URL_RE = /\/static\/(js|css)\/|\.chunk\.(js|css)\b/i;

/** Webpack's own wording, for both flavours: "Loading chunk 42 failed", "Loading CSS chunk 8417 failed". */
const CHUNK_MESSAGE_RE = /loading (css )?chunk\s+\S+\s+failed/i;

/** The parser choking on HTML served in place of JS, across engines. */
const HTML_IN_JS_RE = /unexpected token\s*'?<'?|expected expression, got '</i;

/** Set once we have asked for a reload, so a burst of failures cannot queue several. */
let reloadRequested = false;

const isChunkUrl = (url: unknown): boolean => typeof url === 'string' && CHUNK_URL_RE.test(url);

const messageOf = (error: any): string => (error && typeof error.message === 'string' ? error.message : '');

const nameOf = (error: any): string => (error && typeof error.name === 'string' ? error.name : '');

/**
 * A `<script>`/`<link>` that failed to load reports a *resource* error: it targets the
 * element, carries no `error` object, and only reaches window in the capture phase.
 */
const isChunkResourceTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLScriptElement & HTMLLinkElement;
  if (!element || typeof element.tagName !== 'string') return false;
  const tag = element.tagName.toUpperCase();
  if (tag === 'SCRIPT') return isChunkUrl(element.src);
  if (tag === 'LINK') return isChunkUrl(element.href);
  return false;
};

/**
 * Decide whether an error is a stale-chunk failure rather than an ordinary app bug.
 *
 * `sourceUrl` is the ErrorEvent's `filename` — the script that actually failed — and is
 * only available for window `error` events, never for promise rejections.
 */
export const isChunkLoadFailure = (error: unknown, sourceUrl?: string | null): boolean => {
  const name = nameOf(error);
  const message = messageOf(error);

  // Webpack's named error, raised for both static chunks and React.lazy import() calls.
  if (name === 'ChunkLoadError') return true;
  if (CHUNK_MESSAGE_RE.test(message)) return true;

  // "Unexpected token '<'": the SPA fallback returned index.html and the parser hit the
  // opening tag. Two guards keep this off ordinary app errors: the failing script must be
  // a chunk URL, and JSON.parse failures (whose message also quotes '<' when a server
  // returns HTML to an API call) are excluded — those are app-level bugs, not stale shells.
  if (name === 'SyntaxError' && isChunkUrl(sourceUrl) && HTML_IN_JS_RE.test(message)) {
    return !/json/i.test(message);
  }

  return false;
};

/**
 * Reload once, unless a recovery reload already happened inside the guard window.
 * Returns true when a reload was actually requested (used by the tests).
 */
const attemptRecovery = (reason: string): boolean => {
  if (reloadRequested) return false;

  // By this bug's own mechanics a missing chunk 200s with index.html from CloudFront's SPA
  // fallback, so a stale shell surfaces as a SyntaxError or ChunkLoadError — never as a
  // resource error. A resource error therefore means a genuine network failure, and
  // reloading an offline user drops them on the browser's "No internet" page and destroys
  // their in-page state. navigator.onLine is unreliable when true but reliable when false,
  // which is exactly the direction we need.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    console.warn(`[chunkRecovery] ${reason} while offline — not reloading.`);
    return false;
  }

  let storage: Storage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }

  // Without sessionStorage (Safari private mode, storage disabled) the loop guard cannot
  // be enforced, so we do nothing: a broken page the user can refresh by hand beats a page
  // that refreshes itself forever.
  if (!storage) {
    console.warn(`[chunkRecovery] ${reason} but sessionStorage is unavailable — not reloading.`);
    return false;
  }

  const now = Date.now();
  let lastRecoveryAt = 0;
  try {
    lastRecoveryAt = Number(storage.getItem(RECOVERY_MARKER_KEY)) || 0;
  } catch {
    console.warn(`[chunkRecovery] ${reason} but sessionStorage is unreadable — not reloading.`);
    return false;
  }

  // A marker in the future means the clock stepped backwards after a recovery reload —
  // i.e. we almost certainly *just* reloaded, so this is the most conservative case, not
  // the least. Refuse, and re-stamp with the corrected clock so the guard self-heals on
  // the next failure instead of opening the gate now.
  if (lastRecoveryAt > now) {
    try {
      storage.setItem(RECOVERY_MARKER_KEY, String(now));
    } catch {
      /* nothing more we can do; we are refusing to reload either way */
    }
    console.warn(`[chunkRecovery] ${reason} but the recovery marker is in the future — not reloading.`);
    return false;
  }

  if (now - lastRecoveryAt < RECOVERY_GUARD_MS) {
    console.warn(`[chunkRecovery] ${reason} again ${now - lastRecoveryAt}ms after a recovery reload — giving up.`);
    return false;
  }

  try {
    storage.setItem(RECOVERY_MARKER_KEY, String(now));
  } catch {
    console.warn(`[chunkRecovery] ${reason} but the recovery marker could not be stored — not reloading.`);
    return false;
  }

  reloadRequested = true;
  console.warn(`[chunkRecovery] ${reason} — the app shell is stale; reloading once to pick up current chunks.`);
  window.location.reload();
  return true;
};

const handleError = (event: Event): void => {
  const errorEvent = event as ErrorEvent;

  if (event.target && event.target !== (window as unknown as EventTarget) && isChunkResourceTarget(event.target)) {
    attemptRecovery('a chunk asset failed to load');
    return;
  }

  if (
    isChunkLoadFailure(errorEvent.error, errorEvent.filename) ||
    (typeof errorEvent.message === 'string' && CHUNK_MESSAGE_RE.test(errorEvent.message))
  ) {
    attemptRecovery('a chunk failed to load');
  }
};

/** React.lazy's import() rejects — that arrives as an unhandled rejection, not an error event. */
const handleRejection = (event: Event): void => {
  if (isChunkLoadFailure((event as PromiseRejectionEvent).reason)) {
    attemptRecovery('a lazily imported chunk failed to load');
  }
};

/**
 * Register the global chunk-failure handlers. Call once, before React renders, so a chunk
 * failure during initial hydration is caught too. Returns an uninstall function.
 */
export function installChunkRecovery(): () => void {
  reloadRequested = false;

  // Positive confirmation: if this document is the product of a recovery reload, tag the
  // session. It rides along on existing session/release-health data — no new issue, no new
  // event stream — and makes "did the fix actually fire?" a query rather than an inference
  // from falling error volume.
  try {
    if (window.sessionStorage.getItem(RECOVERY_MARKER_KEY)) {
      Sentry.setTag('chunk_recovery', 'reloaded');
    }
  } catch {
    /* storage unavailable — nothing to report */
  }

  // Capture phase: resource errors on <script>/<link> do not bubble to window.
  window.addEventListener('error', handleError, true);
  window.addEventListener('unhandledrejection', handleRejection);

  return () => {
    window.removeEventListener('error', handleError, true);
    window.removeEventListener('unhandledrejection', handleRejection);
  };
}
